import { easeOutCubic } from './camera';

/**
 * A node's flight between surfaces, as arithmetic.
 *
 * When a popup opens, the node on the map lifts off and lands in a seat at the top of the
 * popup; when a page opens from that popup, it moves on to the bubble the document view draws
 * it in; and each close is the trip back (V0.47). What crosses is a box with a label in it,
 * and the box changes SIZE as well as place — a 13.5 px map node becomes a 16 px seat — so
 * what is interpolated is the box and its metrics together, never a `transform: scale`, which
 * would smear the label. Reading the name on the way across is the entire point.
 *
 * Pure, like `camera.ts`: the DOM half (`app/flight.ts`) measures the two ends, calls this once
 * per frame and writes the answer. That is what lets the crossing be tested without a browser,
 * and it is the same split the camera tween already has.
 */

/** How long the crossing takes. Settled in a prototype at 240 ms — beside the camera's 600. */
export const FLIGHT_MS = 240;

/** A box in viewport pixels. */
export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Everything about a node pill that changes between a map node and a seat. */
export interface PillMetrics {
  fontSize: number;
  padX: number;
  padY: number;
  gap: number;
  swatch: number;
  radius: number;
}

/** One end of a flight: where the box is, and how it is dressed. */
export interface FlightEnd {
  box: Box;
  metrics: PillMetrics;
}

const lerp = (a: number, b: number, e: number): number => a + (b - a) * e;

const lerpBox = (a: Box, b: Box, e: number): Box => ({
  left: lerp(a.left, b.left, e),
  top: lerp(a.top, b.top, e),
  width: lerp(a.width, b.width, e),
  height: lerp(a.height, b.height, e),
});

const lerpMetrics = (a: PillMetrics, b: PillMetrics, e: number): PillMetrics => ({
  fontSize: lerp(a.fontSize, b.fontSize, e),
  padX: lerp(a.padX, b.padX, e),
  padY: lerp(a.padY, b.padY, e),
  gap: lerp(a.gap, b.gap, e),
  swatch: lerp(a.swatch, b.swatch, e),
  radius: lerp(a.radius, b.radius, e),
});

/**
 * The flight part way through.
 *
 * `done` is authoritative and the frame it returns then is `to` EXACTLY — copied, not
 * interpolated to 0.99999 of it. The real box appears the moment the clone is removed, so a
 * clone that merely converged would land a fraction of a pixel off and the swap would show.
 * Same contract as `frameAt` in `camera.ts`, for the same reason.
 */
export function flightAt(
  from: FlightEnd,
  to: FlightEnd,
  elapsed: number,
  duration: number = FLIGHT_MS,
): { box: Box; metrics: PillMetrics; t: number; done: boolean } {
  if (!(duration > 0) || elapsed >= duration) {
    return { box: { ...to.box }, metrics: { ...to.metrics }, t: 1, done: true };
  }
  const t = Math.max(0, elapsed) / duration;
  const e = easeOutCubic(t);
  return {
    box: lerpBox(from.box, to.box, e),
    metrics: lerpMetrics(from.metrics, to.metrics, e),
    t,
    done: false,
  };
}

/**
 * Is `inner` wholly within `outer`, with `margin` to spare on every side?
 *
 * The test that decides whether a map node can be a flight's departure or arrival: a node
 * panned off the clipped stage has a rect but no presence, and a clone that lifted off from
 * it would appear from nowhere. All four edges, like `isInsideStage` in the regression helpers.
 */
export function boxInside(inner: Box, outer: Box, margin = 0): boolean {
  return (
    inner.left >= outer.left + margin &&
    inner.top >= outer.top + margin &&
    inner.left + inner.width <= outer.left + outer.width - margin &&
    inner.top + inner.height <= outer.top + outer.height - margin
  );
}

/**
 * The factor the camera is drawing a map node at.
 *
 * `getBoundingClientRect()` includes the world's `scale(k)`; `getComputedStyle()` does not, so
 * a node's font-size, padding and swatch read at their LAYOUT size while its box is rendered at
 * `k` times that. The clone has to depart dressed the way the node is drawn, or it snaps from a
 * scaled 13.5 px to an unscaled one on its first frame. Guarded against a zero layout width
 * (unmeasured, `display:none`), which is "no scale", not infinity.
 */
export function scaleOf(renderedWidth: number, layoutWidth: number): number {
  return layoutWidth > 0 && Number.isFinite(renderedWidth / layoutWidth)
    ? renderedWidth / layoutWidth
    : 1;
}
